import { AxiosResponse } from "axios";

import {
  IMasterLanguage,
  IMasterTranslation,
  IMasterTranslationKey,
  IRenameMasterKeyRequest,
  ISyncTranslationsRequest,
  ISyncTranslationsResult,
  ITranslationSyncKeyDetail,
  ITranslationSyncStatus,
  IUpsertMasterTranslationRequest,
  IUsedLocationCount,
} from "../../@types/admin/AdminMasterTranslations";
import { api } from "../api";

/**
 * Master translations editor (EN-5178), migrated from the Retool
 * page_Translations app. CRUD on the master translations store — the single
 * source of truth every environment syncs from. The API answers 503 where the
 * master DB is not configured, so failures here can be an environment
 * property, not a bug.
 */
export const masterTranslationsService = {
  /** Every key with its en-GB value and translated languages. */
  async getKeys(): Promise<AxiosResponse<IMasterTranslationKey[]>> {
    return api.get<IMasterTranslationKey[]>("/Retool/Translations/Keys");
  },

  /** Every translation row of one key, across languages and locations. */
  async getByKey(key: string): Promise<AxiosResponse<IMasterTranslation[]>> {
    return api.get<IMasterTranslation[]>(
      `/Retool/Translations/Keys/${encodeURIComponent(key)}`
    );
  },

  /** Language catalogue of the master store. */
  async getLanguages(): Promise<AxiosResponse<IMasterLanguage[]>> {
    return api.get<IMasterLanguage[]>("/Retool/Translations/Languages");
  },

  /** Used locations with how many translations each holds. */
  async getUsedLocations(): Promise<AxiosResponse<IUsedLocationCount[]>> {
    return api.get<IUsedLocationCount[]>("/Retool/Translations/UsedLocations");
  },

  /** Insert or update ONE translation, matched on (key, language, location). */
  async upsertTranslation(
    request: IUpsertMasterTranslationRequest
  ): Promise<AxiosResponse<IMasterTranslation>> {
    return api.put<IMasterTranslation>(
      "/Retool/Translations/Translations",
      request
    );
  },

  /** Rename a key (and set its location) across every language row. */
  async renameKey(
    request: IRenameMasterKeyRequest
  ): Promise<AxiosResponse<void>> {
    return api.put<void>("/Retool/Translations/Keys", request);
  },

  /** Delete a key and every translation it carries. */
  async deleteKey(key: string): Promise<AxiosResponse<void>> {
    return api.delete<void>(
      `/Retool/Translations/Keys/${encodeURIComponent(key)}`
    );
  },

  /** Delete ONE translation. */
  async deleteTranslation(
    key: string,
    languageCode: string,
    usedLocation: string
  ): Promise<AxiosResponse<void>> {
    return api.delete<void>("/Retool/Translations/Translations", {
      params: { key, languageCode, usedLocation },
    });
  },
};

/**
 * Translations sync (EN-5178), the pull-based replacement for the Retool
 * migrate_GlobalTranslations app. Compares the master store with the CURRENT
 * environment's Global DB and copies selected translations into it — unlike
 * Retool, it can never touch another environment.
 */
export const translationsSyncService = {
  /** Per key and language: InSync / Different / Missing in this environment. */
  async getStatus(): Promise<AxiosResponse<ITranslationSyncStatus[]>> {
    return api.get<ITranslationSyncStatus[]>(
      "/Retool/Translations/Sync/Status"
    );
  },

  /** Master vs local values of one key, per language. */
  async getKeyDetail(
    key: string,
    usedLocation: string
  ): Promise<AxiosResponse<ITranslationSyncKeyDetail>> {
    return api.get<ITranslationSyncKeyDetail>(
      `/Retool/Translations/Sync/Keys/${encodeURIComponent(key)}`,
      { params: { usedLocation } }
    );
  },

  /** Pull the selected master translations into this environment. */
  async sync(
    request: ISyncTranslationsRequest
  ): Promise<AxiosResponse<ISyncTranslationsResult>> {
    return api.post<ISyncTranslationsResult>(
      "/Retool/Translations/Sync",
      request
    );
  },
};
